import React, { useMemo } from 'react';
import 'react-table/react-table.css';
import DefaultReactTable, { multiSelectFilterColumn } from '../../../utils/react-table';
import { Dropdown, IconButton } from '../../ui-kit';
import { IconBars, IconFileExcel, IconFileCSV, IconFilter, IconRefresh } from '../../ui-kit/icons';
import { sortBy } from 'lodash';
import { ReactTableDefaults } from 'react-table';

const Toolbar = ({ handleShowFilters, handleRefresh, exportToCSV, exportToXLSX, downloadInProgress, refreshing }) => (
  <div className="hstack gap-1">
    <IconButton Icon={IconRefresh} onClick={handleRefresh} spin={refreshing} title={I18n.t('refresh')} />
    <Dropdown align="end">
      <Dropdown.Toggle variant="link" bsPrefix="none">
        <IconBars />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={handleShowFilters}>
          <IconFilter fixedWidth /> {I18n.t('reports.select_columns')}
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item onClick={exportToCSV} disabled={downloadInProgress}>
          <IconFileCSV fixedWidth /> {I18n.t('reports.download_as_csv')}
        </Dropdown.Item>
        <Dropdown.Item onClick={exportToXLSX} disabled={downloadInProgress}>
          <IconFileExcel fixedWidth /> {I18n.t('reports.download_as_xlsx')}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  </div>
);

const createColumn = (column, data) => {
  const isEnum = column.enum || column.type === 'boolean';
  return isEnum ? filterableColumn(column, data) : generalColumn(column);
};

const generalColumn = column => ({
  Header: <div dangerouslySetInnerHTML={{ __html: column.html_attribute }} />,
  id: column.attribute,
  accessor: d => d[column.attribute].value,
  filterable: column.filterable,
  sortable: column.sortable,
  width: column.width,
  Cell: row => {
    let className = 'general-report-cell';
    if (column.align != 'start') className += ` d-flex align-items-center justify-content-${column.align}`;
    let value = row.original[column.attribute].formatted_value;
    if (Array.isArray(value)) value = value.join('&nbsp;');

    return <div className={className} dangerouslySetInnerHTML={{ __html: value }} />;
  }
});

const uniqueValues = (data, column) => {
  const { attribute, type } = column;

  if (type === 'boolean') {
    return [
      { value: true, html: I18n.t('yes_word') },
      { value: false, html: I18n.t('no_word') }
    ];
  }

  let result = {};
  for (const d of data) {
    const value = d[attribute].value;
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      const formattedValues = d[attribute].filtering_label || d[attribute].formatted_value || [];
      for (const [i, v] of value.entries()) {
        const formattedValue = formattedValues[i] || v;
        result[v] = result[v] || {
          value: v,
          html: <div dangerouslySetInnerHTML={{ __html: formattedValue }} />
        };
      }
    } else {
      result[value] = result[value] || {
        value,
        html: <div dangerouslySetInnerHTML={{ __html: d[attribute].formatted_value }} />
      };
    }
  }

  for (const key of Object.keys(result)) {
    if (result[key].value === undefined || result[key].value === null) {
      delete result[key];
    }
  }

  return sortBy(Object.values(result), 'value');
};

const filterableColumn = (column, data) =>
  multiSelectFilterColumn({
    ...generalColumn(column),
    selectOptions: uniqueValues(data, column),
    includeEmpty: column.type !== 'boolean'
  });

const TableReport = ({
  columnsByKey,
  data,
  columnKeys,
  handleShowFilters,
  handleRefresh,
  exportToCSV,
  exportToXLSX,
  downloadInProgress,
  refreshing,
  onFilteredChange,
  onSortedChange
}) => {
  data = data || [];
  columnKeys = columnKeys || [];
  const selectedColumns = columnKeys.map(key => columnsByKey[key]).filter(Boolean);

  const PaginationComponent = props => (
    <div className="hstack gap-1 bg-light-subtle">
      <div className="flex-grow-1">
        <ReactTableDefaults.PaginationComponent {...props} />
      </div>
      <div className="flex-shrink-0">
        <Toolbar
          handleShowFilters={handleShowFilters}
          handleRefresh={handleRefresh}
          exportToCSV={exportToCSV}
          exportToXLSX={exportToXLSX}
          downloadInProgress={downloadInProgress}
          refreshing={refreshing}
        />
      </div>
    </div>
  );

  const columns = useMemo(() => selectedColumns.map(column => createColumn(column, data)), [selectedColumns, data]);

  return (
    <DefaultReactTable
      PaginationComponent={PaginationComponent}
      showPaginationTop={true}
      filterable
      onFilteredChange={filtered => onFilteredChange?.(filtered)}
      onSortedChange={sorted => onSortedChange?.(sorted)}
      data={data}
      columns={columns}
    />
  );
};

export default TableReport;
